import React, { useState, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import { FiList, FiEdit2, FiX, FiSearch, FiDownload, FiUpload, FiCheckCircle, FiAlertCircle, FiXCircle, FiImage, FiExternalLink, FiCopy } from 'react-icons/fi'
import { MaterialReactTable } from 'material-react-table'
import { Modal } from 'antd'
import * as XLSX from 'xlsx'
import { 
  getAllItems, 
  getSectores, 
  getCategorias, 
  getSubCategorias, 
  getMarcas, 
  getSegmentos, 
  getSubSegmentos, 
  getFamilias,
  putAprobarJerarquia,
  postCargarImagenItem,
  getIam,
  getIamRol,
  postExportarItems,
  putActualizarGrupoArticulo,
  putActualizarTextoBreve,
  putActualizarFob,
  putActualizarCantidad,
  getGruposArticulos,
  postImportarNPF,
  getImagenesPorMaterial
} from '../../services/axiomaService'
import './AxiomaJerarquiasContainer.css'

const AxiomaJerarquiasContainer = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [itemSeleccionado, setItemSeleccionado] = useState(null)
  const [filtroAprobada, setFiltroAprobada] = useState('todos') // 'todos', 'si', 'no'
  const [filtroEstado, setFiltroEstado] = useState('todos') // 'todos', 'no_existe', 'existe_previamente', 'creado'
  
  // Estados para la imagen
  const [imagenUrl, setImagenUrl] = useState(null)
  const [loadingImagen, setLoadingImagen] = useState(false)
  
  // Estados para exportación
  const [itemsSeleccionados, setItemsSeleccionados] = useState([])
  const [modalExportarVisible, setModalExportarVisible] = useState(false)
  const [loadingExportar, setLoadingExportar] = useState(false)
  const [loadingImportarNPF, setLoadingImportarNPF] = useState(false)
  const inputArchivoNPFRef = React.useRef(null)
  
  // Estados para modal de información SAP
  const [modalSapVisible, setModalSapVisible] = useState(false)
  const [itemSapSeleccionado, setItemSapSeleccionado] = useState(null)
  
  // Estados para los selects
  const [sectores, setSectores] = useState([])
  const [categorias, setCategorias] = useState([])
  const [subcategorias, setSubcategorias] = useState([])
  const [marcas, setMarcas] = useState([])
  const [segmentos, setSegmentos] = useState([])
  const [subsegmentos, setSubsegmentos] = useState([])
  const [familias, setFamilias] = useState([])
  
  // Estados para los valores seleccionados
  const [valoresSeleccionados, setValoresSeleccionados] = useState({
    sector: '',
    categoria: '',
    subcategoria: '',
    marca: '',
    segmento: '',
    subsegmento: '',
    familia: ''
  })
  
  // Estados para edición en línea
  const [editingCell, setEditingCell] = useState(null)
  const [editingValue, setEditingValue] = useState('')
  
  // Estado para filtro de búsqueda de familia
  const [busquedaFamilia, setBusquedaFamilia] = useState('')
  
  // Estados para grupos de artículos
  const [gruposArticulos, setGruposArticulos] = useState([])
  const [loadingGrupos, setLoadingGrupos] = useState(false)

  // Estados del módulo independiente de imágenes
  const [imagenesPanelVisible, setImagenesPanelVisible] = useState(false)
  const [imagenesData, setImagenesData] = useState([])
  const [imagenesLoading, setImagenesLoading] = useState(false)
  const [imagenesError, setImagenesError] = useState('')
  const [imagenesFiltroId, setImagenesFiltroId] = useState('')
  const [imagenesFiltroAplicado, setImagenesFiltroAplicado] = useState('')
  const [imagenesBusquedaEjecutada, setImagenesBusquedaEjecutada] = useState(false)
  
  // Referencia para evitar actualizaciones duplicadas
  const actualizandoGrupoArticulo = React.useRef(new Set())

  useEffect(() => {
    const cargarItems = async () => {
      setLoading(true)
      try {
        const response = await getAllItems()
        
        if (response && response.items) {
          setItems(response.items)
          console.log('Items cargados:', response.items)
        } else {
          toast.error('Error al cargar los items')
        }
      } catch (error) {
        console.error('Error al cargar items:', error)
        toast.error('Error al cargar los items')
      } finally {
        setLoading(false)
      }
    }

    cargarItems()
  }, [])

  useEffect(() => {
    const obtenerRolUsuario = async () => {
      try {
        // Obtener información del usuario actual
        const userInfo = await getIam()
        
        if (userInfo && userInfo.id) {
          console.log('=== Obteniendo rol del usuario en Jerarquías ===')
          console.log('Usuario info:', userInfo)
          
          // Obtener el rol del usuario
          await getIamRol(userInfo.id)
        }
      } catch (error) {
        console.error('Error al obtener rol del usuario:', error)
      }
    }

    obtenerRolUsuario()
  }, [])

  useEffect(() => {
    const cargarGruposArticulos = async () => {
      setLoadingGrupos(true)
      try {
        const gruposResponse = await getGruposArticulos()
        if (gruposResponse && gruposResponse.grupo_articulos) {
          setGruposArticulos(gruposResponse.grupo_articulos)
        }
      } catch (error) {
        console.error('Error al cargar grupos de artículos:', error)
        toast.error('Error al cargar los grupos de artículos')
      } finally {
        setLoadingGrupos(false)
      }
    }

    cargarGruposArticulos()
  }, [])

  const parseJerarquia = (jerarquiaString) => {
    if (!jerarquiaString) return []
    return jerarquiaString.split('°').map(val => val.trim())
  }

  const getEstadoInfo = (estado) => {
    switch (estado) {
      case 'no_existe':
        return {
          icon: FiXCircle,
          color: '#ef4444', // rojo
          label: 'No Existe',
          tooltip: 'No Existe'
        }
      case 'existe_previamente':
        return {
          icon: FiCheckCircle,
          color: '#f59e0b', // amarillo
          label: 'Existe Previamente',
          tooltip: 'Existe Previamente'
        }
      case 'creado':
        return {
          icon: FiCheckCircle,
          color: '#10b981', // verde
          label: 'Creado',
          tooltip: 'Creado'
        }
      default:
        return {
          icon: FiAlertCircle,
          color: '#9ca3af', // gris
          label: 'Desconocido',
          tooltip: 'Estado desconocido'
        }
    }
  }

  const cargarDatosSelects = async () => {
    try {
      const [
        sectoresData,
        categoriasData,
        subcategoriasData,
        marcasData,
        segmentosData,
        subsegmentosData,
        familiasData
      ] = await Promise.all([
        getSectores(),
        getCategorias(),
        getSubCategorias(),
        getMarcas(),
        getSegmentos(),
        getSubSegmentos(),
        getFamilias()
      ])

      if (sectoresData && sectoresData.sectores) setSectores(sectoresData.sectores)
      if (categoriasData && categoriasData.categorias) setCategorias(categoriasData.categorias)
      if (subcategoriasData && subcategoriasData.subcategorias) setSubcategorias(subcategoriasData.subcategorias)
      if (marcasData && marcasData.marcas) setMarcas(marcasData.marcas)
      if (segmentosData && segmentosData.segmentos) setSegmentos(segmentosData.segmentos)
      if (subsegmentosData && subsegmentosData.subsegmentos) setSubsegmentos(subsegmentosData.subsegmentos)
      if (familiasData && familiasData.familias) setFamilias(familiasData.familias)
    } catch (error) {
      console.error('Error al cargar datos de selects:', error)
      toast.error('Error al cargar las opciones')
    }
  }

  const handleAbrirModal = async (item) => {
    setItemSeleccionado(item)
    setModalVisible(true)
    
    // Cargar datos de los selects primero
    await cargarDatosSelects()
    
    // Luego parsear y setear jerarquía actual
    const valores = parseJerarquia(item.jerarquia)
    console.log('Valores parseados:', valores)
    console.log('Jerarquía completa:', item.jerarquia)
    
    const familiaValor = valores[6] || ''
    setBusquedaFamilia(familiaValor)
    
    setValoresSeleccionados({
      sector: valores[0] || '',
      categoria: valores[1] || '',
      subcategoria: valores[2] || '',
      marca: valores[3] || '',
      segmento: valores[4] || '',
      subsegmento: valores[5] || '',
      familia: familiaValor
    })
  }

  const handleCerrarModal = () => {
    setModalVisible(false)
    setItemSeleccionado(null)
    setImagenUrl(null)
    setBusquedaFamilia('')
    setValoresSeleccionados({
      sector: '',
      categoria: '',
      subcategoria: '',
      marca: '',
      segmento: '',
      subsegmento: '',
      familia: ''
    })
  }

  const handleChangeSelect = (nivel, valor) => {
    setValoresSeleccionados(prev => ({
      ...prev,
      [nivel]: valor
    }))
  }

  const handleAprobarJerarquia = async () => {
    if (!itemSeleccionado) return

    // Construir la jerarquía concatenada desde los valores seleccionados
    const jerarquiaConcatenada = [
      valoresSeleccionados.sector,
      valoresSeleccionados.categoria,
      valoresSeleccionados.subcategoria,
      valoresSeleccionados.marca,
      valoresSeleccionados.segmento,
      valoresSeleccionados.subsegmento,
      valoresSeleccionados.familia,
    ].join('°')

    console.log('Aprobando jerarquía:', jerarquiaConcatenada)

    try {
      const response = await putAprobarJerarquia(itemSeleccionado.id, jerarquiaConcatenada)
      
      if (response) {
        toast.success('Jerarquía aprobada exitosamente')
        
        // Actualizar el item en la lista local
        setItems(prevItems =>
          prevItems.map(item =>
            item.id === itemSeleccionado.id
              ? { ...item, jerarquia_aprobada: 1, jerarquia: jerarquiaConcatenada }
              : item
          )
        )
        
        // Actualizar el item seleccionado
        setItemSeleccionado(prev => ({
          ...prev,
          jerarquia_aprobada: 1,
          jerarquia: jerarquiaConcatenada
        }))
      } else {
        toast.error('Error al aprobar la jerarquía. Por favor, intenta nuevamente.')
      }
    } catch (error) {
      console.error('Error al aprobar jerarquía:', error)
      toast.error('Error al aprobar la jerarquía. Por favor, intenta nuevamente.')
    }
  }

  const handleCargarImagen = async () => {
    if (!itemSeleccionado || !itemSeleccionado.gs_url) {
      toast.warning('Este item no tiene gs_url disponible')
      return
    }

    setLoadingImagen(true)
    console.log('=== Cargando imagen del item ===')
    console.log('Item ID:', itemSeleccionado.id)
    console.log('gs_url:', itemSeleccionado.gs_url)

    try {
      const response = await postCargarImagenItem(itemSeleccionado.gs_url)
      
      console.log('=== Respuesta completa ===')
      console.log(response)
      
      if (response && response.gs_url_signed) {
        setImagenUrl(response.gs_url_signed)
        toast.success('Imagen cargada exitosamente')
      } else {
        toast.error('Error al cargar la imagen')
      }
    } catch (error) {
      console.error('Error al cargar imagen:', error)
      toast.error('Error al cargar la imagen')
    } finally {
      setLoadingImagen(false)
    }
  }

  const handleSeleccionarItem = (itemId) => {
    setItemsSeleccionados(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId)
      }
      return [...prev, itemId]
    })
  }

  const handleSeleccionarTodos = (itemsVisibles) => {
    if (itemsSeleccionados.length === itemsVisibles.length) {
      setItemsSeleccionados([])
    } else {
      setItemsSeleccionados(itemsVisibles.map(item => item.id))
    }
  }

  const handleAbrirModalExportar = () => {
    if (itemsSeleccionados.length === 0) {
      toast.warning('Selecciona al menos un item para exportar')
      return
    }
    setModalExportarVisible(true)
  }

  const descargarExcelSeleccionados = () => {
    // Obtener los items seleccionados
    const itemsParaExcel = items.filter(item => itemsSeleccionados.includes(item.id))
    
    // Mapear los datos al formato de la tabla
    const datosExcel = itemsParaExcel.map(item => {
      const estadoInfo = getEstadoInfo(item.estado)
      return {
        'Jerarquía Aprobada': item.jerarquia_aprobada ? 'Sí' : 'No',
        'Estado': estadoInfo.label || '',
        'Tipo Material': item.tipo_material || '',
        'Texto Breve': item.texto_breve_es || '',
      'Grupo Artículo': item.grupo_articulo || '',
      'Material Vigente': item.material_vigente || '',
      'NRO Antiguo de Material (NPF)': item.npf || '',
      'Peso Bruto': item.peso_bruto || '',
      'Unidad de Peso': item.unidad_peso || '',
      'Peso Neto': item.peso_neto || '',
      'Volumen': item.volumen || '',
      'Unidad de Volumen': item.unidad_volumen || '',
      'Tamaño Dimensión': item.tamanio_dimension || '',
      'Texto Chino': item.texto_chino || '',
      'Inditivo': item.motivo || '',
      'Jerarquía': item.jerarquia || '',
      'FOB': item.fob || '',
      'Cantidad': item.cantidad ?? 0,
      'Nombre de Proveedor': item.nombre_proveedor || '',
      'Código de Proveedor': item.codigo_proveedor || '',
      'Marca': item.marca || '',
      'Modelo de Aplicación': item.modelo_aplicacion || '',
      'Pedido Inicial': item.pedido_inicial ?? '',
      'País de Origen': item.pais_origen || '',
      'Solicitado Por': item.solicitado_por || '',
      'SAP Asignado': item.sap_asignado || '',
      'Planificación (DISPO)': item.planificador || '',
      'Grupo de Compras': item.grupo_compras || '',
      'Descripción Excel': item.texto_breve_en || '',
      'Fecha Edición Manual': item.fecha_edicion_manual || '',
      'Creado En': item.creado_en || '',
      }
    })

    // Crear el archivo Excel
    const worksheet = XLSX.utils.json_to_sheet(datosExcel)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Jerarquías')
    
    // Generar nombre de archivo con fecha
    const fechaActual = new Date().toISOString().split('T')[0]
    const nombreArchivo = `Jerarquias_Exportadas_${fechaActual}.xlsx`
    
    // Descargar el archivo
    XLSX.writeFile(workbook, nombreArchivo)
  }

  const descargarExcelNPFSeleccionados = () => {
    const itemsParaExcel = items.filter(item => itemsSeleccionados.includes(item.id))

    const datosExcel = itemsParaExcel.map(item => ({
      NPF: item.npf || '',
      INFALTABLE: item.infaltable ?? '',
      GARANTIA: item.garantia ?? '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(datosExcel)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'NPF')

    const fechaActual = new Date().toISOString().split('T')[0]
    const nombreArchivo = `NPF_Exportados_${fechaActual}.xlsx`

    XLSX.writeFile(workbook, nombreArchivo)
  }

  const handleClickImportarNPF = () => {
    inputArchivoNPFRef.current?.click()
  }

  const handleArchivoNPFChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLoadingImportarNPF(true)
    try {
      const result = await postImportarNPF(file)
      if (result) {
        toast.success('Archivo NPF importado correctamente')
        const itemsResponse = await getAllItems()
        if (itemsResponse?.items) setItems(itemsResponse.items)
      } else {
        toast.error('Error al importar el archivo NPF')
      }
    } catch (err) {
      toast.error('Error al importar el archivo NPF')
    } finally {
      setLoadingImportarNPF(false)
      e.target.value = ''
    }
  }

  const handleExportarItems = async () => {
    setLoadingExportar(true)
    try {
      const response = await postExportarItems(itemsSeleccionados)
      
      if (response) {
        toast.success(`${itemsSeleccionados.length} items exportados exitosamente`)
        
        // Descargar Excel con los items seleccionados
        descargarExcelSeleccionados()
        
        setModalExportarVisible(false)
        setItemsSeleccionados([])
        
        // Recargar los items
        const itemsResponse = await getAllItems()
        if (itemsResponse && itemsResponse.items) {
          setItems(itemsResponse.items)
        }
      } else {
        toast.error('Error al exportar los items')
      }
    } catch (error) {
      console.error('Error al exportar items:', error)
      toast.error('Error al exportar los items')
    } finally {
      setLoadingExportar(false)
    }
  }

  const normalizarImagenesPorMaterial = (payload) => {
    const listaCruda = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.success)
        ? payload.success
        : Array.isArray(payload?.data)
          ? payload.data
          : []

    return listaCruda.map((item, index) => {
      const materialId = String(
        item?.codigo_material ||
        item?.id_material ||
        item?.material_id ||
        item?.material ||
        `material-${index + 1}`
      )

      const imagenesCrudas = Array.isArray(item?.imagenes) ? item.imagenes : []
      const imagenes = imagenesCrudas
        .map((img, imgIndex) => {
          const publicUrl = img?.public_url || img?.url || img?.signed_url || ''
          const gsUrl = img?.gs_url || ''
          return {
            id: `${materialId}-img-${imgIndex}`,
            publicUrl,
            gsUrl,
          }
        })
        .filter((img) => Boolean(img.publicUrl))

      return {
        materialId,
        imagenes,
      }
    })
  }

  const cargarImagenesPorMaterial = async () => {
    setImagenesLoading(true)
    setImagenesError('')
    try {
      const response = await getImagenesPorMaterial()
      if (!response) {
        throw new Error('No se recibió información de imágenes')
      }

      const payload = response?.materiales || response
      const dataNormalizada = normalizarImagenesPorMaterial(payload)
      setImagenesData(dataNormalizada)
    } catch (error) {
      setImagenesError('No fue posible cargar las imágenes por material.')
      toast.error('Error al cargar imágenes por material')
    } finally {
      setImagenesLoading(false)
    }
  }

  const handleTogglePanelImagenes = async () => {
    const nuevoEstado = !imagenesPanelVisible
    setImagenesPanelVisible(nuevoEstado)

    if (nuevoEstado && imagenesData.length === 0 && !imagenesLoading) {
      await cargarImagenesPorMaterial()
    }
  }

  const handleCopiarEnlace = async (url) => {
    if (!url) return

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url)
      } else {
        const inputTemporal = document.createElement('textarea')
        inputTemporal.value = url
        inputTemporal.style.position = 'fixed'
        inputTemporal.style.left = '-9999px'
        document.body.appendChild(inputTemporal)
        inputTemporal.select()
        document.execCommand('copy')
        document.body.removeChild(inputTemporal)
      }
      toast.success('Enlace copiado al portapapeles')
    } catch (error) {
      toast.error('No se pudo copiar el enlace')
    }
  }

  const handleBuscarImagenes = async () => {
    const materialId = imagenesFiltroId.trim()

    if (!materialId) {
      toast.warning('Digita un id de material para buscar')
      return
    }

    setImagenesBusquedaEjecutada(true)
    setImagenesFiltroAplicado(materialId)
  }

  const materialesFiltradosImagenes = useMemo(() => {
    if (!imagenesBusquedaEjecutada) return []
    const filtro = imagenesFiltroAplicado.trim().toLowerCase()
    if (!filtro) return []
    return imagenesData.filter((item) =>
      String(item.materialId).toLowerCase().includes(filtro)
    )
  }, [imagenesData, imagenesBusquedaEjecutada, imagenesFiltroAplicado])

  const nivelesConfig = [
    { key: 'sector', label: 'Sector', opciones: sectores, campo: 'nombre' },
    { key: 'categoria', label: 'Categoría', opciones: categorias, campo: 'nombre' },
    { key: 'subcategoria', label: 'Subcategoría', opciones: subcategorias, campo: 'nombre' },
    { key: 'marca', label: 'Marca', opciones: marcas, campo: 'nombre' },
    { key: 'segmento', label: 'Segmento', opciones: segmentos, campo: 'nombre' },
    { key: 'subsegmento', label: 'Subsegmento', opciones: subsegmentos, campo: 'nombre' },
    { key: 'familia', label: 'Familia', opciones: familias, campo: 'nombre' },
  ]

  // Filtrar items según el filtro de jerarquía aprobada y estado
  const itemsFiltrados = useMemo(() => {
    let itemsFiltradosResult = items
    
    // Filtro por jerarquía aprobada
    if (filtroAprobada === 'si') {
      itemsFiltradosResult = itemsFiltradosResult.filter(item => item.jerarquia_aprobada === 1)
    } else if (filtroAprobada === 'no') {
      itemsFiltradosResult = itemsFiltradosResult.filter(item => item.jerarquia_aprobada !== 1)
    }
    
    // Filtro por estado
    if (filtroEstado !== 'todos') {
      itemsFiltradosResult = itemsFiltradosResult.filter(item => item.estado === filtroEstado)
    }
    
    return itemsFiltradosResult
  }, [items, filtroAprobada, filtroEstado])

  const columns = useMemo(
    () => [
      // 1. ORDEN COLUMNA (seleccionar)
      {
        accessorKey: 'seleccionar',
        header: () => (
          <input
            type="checkbox"
            checked={itemsFiltrados.length > 0 && 
                     itemsSeleccionados.length === itemsFiltrados.length}
            onChange={() => handleSeleccionarTodos(itemsFiltrados)}
            style={{ cursor: 'pointer' }}
          />
        ),
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={itemsSeleccionados.includes(row.original.id)}
            onChange={() => handleSeleccionarItem(row.original.id)}
            style={{ cursor: 'pointer' }}
          />
        ),
      },
      // 2. INFALTABLE
      {
        accessorKey: 'infaltable',
        header: 'Infaltable',
        size: 90,
        Cell: ({ cell }) => {
          const value = cell.getValue()
          const num = Number(value)
          return Number.isFinite(num) ? num : 0
        },
      },
      // 3. GARANTÍA
      {
        accessorKey: 'garantia',
        header: 'Garantía',
        size: 90,
        Cell: ({ cell }) => {
          const value = cell.getValue()
          const num = Number(value)
          return Number.isFinite(num) ? num : 0
        },
      },
      // 4. ACCIÓN
      {
        accessorKey: 'accion',
        header: 'Acción',
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => handleAbrirModal(row.original)}
            className="jerarquias-action-btn"
            title="Ver jerarquía"
          >
            <FiEdit2 />
            Ver
          </button>
        ),
      },
       // 32. ESTADO
       {
        accessorKey: 'estado',
        header: 'Estado',
        size: 120,
        Cell: ({ cell, row }) => {
          const estado = cell.getValue()
          const estadoInfo = getEstadoInfo(estado)
          const IconComponent = estadoInfo.icon
          const esClickable = estado === 'existe_previamente' || estado === 'creado'
          
          const handleClickEstado = () => {
            if (esClickable) {
              setItemSapSeleccionado(row.original)
              setModalSapVisible(true)
            }
          }
          
          if (esClickable) {
            return (
              <button
                type="button"
                onClick={handleClickEstado}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  outline: 'none',
                  border: 'none',
                  background: 'transparent',
                  padding: '4px',
                  borderRadius: '4px'
                }}
                title={estadoInfo.tooltip}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.7'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = '2px solid #667eea'
                  e.currentTarget.style.outlineOffset = '2px'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none'
                }}
              >
                <IconComponent 
                  size={18} 
                  color={estadoInfo.color}
                  style={{ flexShrink: 0 }}
                />
              </button>
            )
          }
          
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'default'
              }}
              title={estadoInfo.tooltip}
            >
              <IconComponent 
                size={18} 
                color={estadoInfo.color}
                style={{ flexShrink: 0 }}
              />
            </div>
          )
        },
      },
      // 3. JERARQUÍA APROBADA
      {
        accessorKey: 'jerarquia_aprobada',
        header: 'Jerarquía Aprobada',
        size: 140,
        Cell: ({ cell }) => (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: cell.getValue() ? '#d1fae5' : '#fef3c7',
            color: cell.getValue() ? '#065f46' : '#92400e'
          }}>
            {cell.getValue() ? 'Sí' : 'No'}
          </span>
        ),
      },
      // 4. TIPO DE MATERIAL
      {
        accessorKey: 'tipo_material',
        header: 'Tipo Material',
        size: 120,
      },
      // 5. TEXTO BREVE
      {
        accessorKey: 'texto_breve_es',
        header: 'Texto Breve',
        size: 250,
        Cell: ({ cell, row }) => {
          const cellKey = `texto_breve_${row.original.id}`
          const isEditing = editingCell === cellKey
          const currentValue = isEditing ? editingValue : (cell.getValue() || '')
          
          const handleKeyDown = async (e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              const nuevoValor = e.target.value.trim()
              const valorAnterior = cell.getValue() || ''
              
              if (nuevoValor.length > 40) {
                toast.error('El texto breve no puede superar 40 caracteres')
                return
              }
              
              if (nuevoValor === valorAnterior) {
                setEditingCell(null)
                return
              }
              
              const confirmar = window.confirm(
                `¿Estás seguro que deseas actualizar el texto breve?\n\nValor anterior: ${valorAnterior || '(vacío)'}\nValor nuevo: ${nuevoValor || '(vacío)'}`
              )
              
              if (confirmar) {
                try {
                  const valorGuardar = nuevoValor.slice(0, 40)
                  const response = await putActualizarTextoBreve(row.original.id, valorGuardar)
                  if (response) {
                    toast.success('Texto breve actualizado correctamente')
                    // Actualizar el item en la lista local
                    setItems(prevItems =>
                      prevItems.map(item =>
                        item.id === row.original.id
                          ? { ...item, texto_breve_es: valorGuardar }
                          : item
                      )
                    )
                    setEditingCell(null)
                  } else {
                    toast.error('Error al actualizar el texto breve')
                    setEditingCell(null)
                  }
                } catch (error) {
                  console.error('Error al actualizar texto breve:', error)
                  toast.error('Error al actualizar el texto breve')
                  setEditingCell(null)
                }
              } else {
                setEditingCell(null)
              }
            } else if (e.key === 'Escape') {
              setEditingCell(null)
            }
          }
          
          const handleClick = () => {
            setEditingCell(cellKey)
            setEditingValue(cell.getValue() || '')
          }
          
          const handleBlur = () => {
            setEditingCell(null)
          }
          
          if (isEditing) {
            return (
              <input
                type="text"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value.slice(0, 40))}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                maxLength={40}
                placeholder={`Máx. 40 caracteres (${(editingValue || '').length}/40)`}
                style={{
                  width: '100%',
                  border: '2px solid #667eea',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            )
          }
          
          return (
            <div
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick()
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                width: '100%',
                padding: '4px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                minHeight: '24px',
                border: '1px solid transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
                e.currentTarget.style.borderColor = '#e5e7eb'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.borderColor = 'transparent'
              }}
            >
              {currentValue || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Click para editar</span>}
            </div>
          )
        },
      },
      // 6. GRUPO DE ARTÍCULO
      {
        accessorKey: 'grupo_articulo',
        header: 'Grupo Artículo',
        size: 150,
        Cell: ({ cell, row }) => {
          const cellKey = `grupo_articulo_${row.original.id}`
          const isEditing = editingCell === cellKey
          const currentValue = cell.getValue() || ''
          
          // Función para actualizar el grupo artículo
          const handleActualizarGrupoArticulo = async (itemId, nuevoValor, valorAnterior) => {
            // Evitar actualizaciones duplicadas
            if (actualizandoGrupoArticulo.current.has(itemId)) {
              return
            }
            
            if (nuevoValor === valorAnterior) {
              return
            }
            
            // Marcar como en proceso
            actualizandoGrupoArticulo.current.add(itemId)
            
            const confirmar = window.confirm(
              `¿Estás seguro que deseas actualizar el grupo artículo?\n\nValor anterior: ${valorAnterior || '(vacío)'}\nValor nuevo: ${nuevoValor || '(vacío)'}`
            )
            
            if (confirmar) {
              try {
                const response = await putActualizarGrupoArticulo(itemId, nuevoValor)
                if (response) {
                  toast.success('Grupo artículo actualizado correctamente')
                  // Actualizar el item en la lista local
                  setItems(prevItems =>
                    prevItems.map(item =>
                      item.id === itemId
                        ? { ...item, grupo_articulo: nuevoValor }
                        : item
                    )
                  )
                } else {
                  toast.error('Error al actualizar el grupo artículo')
                }
              } catch (error) {
                console.error('Error al actualizar grupo artículo:', error)
                toast.error('Error al actualizar el grupo artículo')
              } finally {
                // Remover de la lista de procesos
                actualizandoGrupoArticulo.current.delete(itemId)
              }
            } else {
              // Si cancela, también remover
              actualizandoGrupoArticulo.current.delete(itemId)
            }
          }
          
          // Buscar el grupo en la lista para mostrar "nombre - descripción"
          const grupoActual = gruposArticulos.find(g => {
            const id = g.grupo_articulo || g.grupo_articulos
            return id === currentValue
          })
          
          const displayValue = grupoActual 
            ? (() => {
                const id = grupoActual.grupo_articulo || grupoActual.grupo_articulos
                const nombre = grupoActual.descripcion_grupo_aticulo || grupoActual.descripcion_grupo_articulo || ''
                return `${id} - ${nombre}`
              })()
            : currentValue
          
          const handleChange = (e) => {
            const inputValue = e.target.value
            setEditingValue(inputValue)
            // No actualizar aquí, solo actualizar el valor del input
            // La actualización se hará en onBlur o cuando se seleccione del datalist
          }
          
          const handleBlur = async (e) => {
            // Al perder el foco, si tiene formato "ID - NOMBRE", extraer solo el ID
            const inputValue = e.target.value
            const valorAnterior = cell.getValue() || ''
            
            if (inputValue.includes(' - ')) {
              const id = inputValue.split(' - ')[0].trim()
              if (id !== valorAnterior) {
                await handleActualizarGrupoArticulo(row.original.id, id, valorAnterior)
              }
            } else if (inputValue.trim() && inputValue !== valorAnterior) {
              // Si el usuario escribió algo que no tiene el formato, buscar coincidencia exacta
              const grupoCoincidencia = gruposArticulos.find(g => {
                const id = g.grupo_articulo || g.grupo_articulos
                const nombre = g.descripcion_grupo_aticulo || g.descripcion_grupo_articulo || ''
                return inputValue === `${id} - ${nombre}` || inputValue === id
              })
              
              if (grupoCoincidencia) {
                const id = grupoCoincidencia.grupo_articulo || grupoCoincidencia.grupo_articulos
                if (id !== valorAnterior) {
                  await handleActualizarGrupoArticulo(row.original.id, id, valorAnterior)
                }
              }
            }
            
            setEditingCell(null)
          }
          
          const handleClick = () => {
            setEditingCell(cellKey)
            setEditingValue(displayValue)
          }
          
          if (isEditing) {
            const inputId = `grupo-articulo-input-${row.original.id}`
            return (
              <div>
                <label htmlFor={inputId} className="sr-only">
                  Grupo Artículo
                </label>
                <input
                  id={inputId}
                  type="text"
                  value={editingValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{
                    width: '100%',
                    border: '2px solid #667eea',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  disabled={loadingGrupos}
                  list={`grupo-articulos-list-${row.original.id}`}
                  placeholder="Escribe para buscar..."
                />
                <datalist id={`grupo-articulos-list-${row.original.id}`} aria-label="Opciones de grupo de artículo">
                  {gruposArticulos.map((grupo) => {
                    const id = grupo.grupo_articulo || grupo.grupo_articulos
                    const nombre = grupo.descripcion_grupo_aticulo || grupo.descripcion_grupo_articulo || ''
                    const optionValue = `${id} - ${nombre}`
                    return (
                      <option key={id || `grupo-${grupo.id}`} value={optionValue} data-id={id} />
                    )
                  })}
                </datalist>
              </div>
            )
          }
          
          return (
            <div
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick()
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                width: '100%',
                padding: '4px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                minHeight: '24px',
                border: '1px solid transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
                e.currentTarget.style.borderColor = '#e5e7eb'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.borderColor = 'transparent'
              }}
            >
              {displayValue || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Click para editar</span>}
            </div>
          )
        },
      },
      // 7. MATERIAL VIGENTE
      {
        accessorKey: 'material_vigente',
        header: 'Material Vigente',
        size: 130,
      },
      // 8. NRO ANTIGUO DE MATERIAL (NPF)
      {
        accessorKey: 'npf',
        header: 'NRO Antiguo de Material (NPF)',
        size: 200,
      },
      // 9. PESO BRUTO
      {
        accessorKey: 'peso_bruto',
        header: 'Peso Bruto',
        size: 100,
      },
      // 10. UNIDAD DE PESO
      {
        accessorKey: 'unidad_peso',
        header: 'Unidad de Peso',
        size: 100,
      },
      // 11. PESO NETO
      {
        accessorKey: 'peso_neto',
        header: 'Peso Neto',
        size: 100,
      },
      // 12. VOLUMEN
      {
        accessorKey: 'volumen',
        header: 'Volumen',
        size: 100,
      },
      // 13. UNIDAD DE VOLUMEN
      {
        accessorKey: 'unidad_volumen',
        header: 'Unidad de Volumen',
        size: 120,
      },
      // 14. TAMAÑO DIMENSIÓN
      {
        accessorKey: 'tamanio_dimension',
        header: 'Tamaño Dimensión',
        size: 180,
      },
      // 15. Texto Chino
      {
        accessorKey: 'texto_breve_en',
        header: 'Texto Breve Inglés',
        size: 250,
      },
      {
        accessorKey: 'texto_chino',
        header: 'Texto Chino',
        size: 200,
      },
      // 16. INDITIVO (Motivo)
      {
        accessorKey: 'motivo',
        header: 'Motivo',
        size: 150,
      },
      // 17. JERARQUÍA
      {
        accessorKey: 'jerarquia',
        header: 'Jerarquía',
        size: 300,
        Cell: ({ cell }) => (
          <div style={{ fontSize: '11px', fontFamily: 'monospace' }}>
            {cell.getValue() || '-'}
          </div>
        ),
      },
      // 18. FOB
      {
        accessorKey: 'fob',
        header: 'FOB',
        size: 100,
        Cell: ({ cell, row }) => {
          const cellKey = `fob_${row.original.id}`
          const isEditing = editingCell === cellKey
          const currentValue = isEditing ? editingValue : (cell.getValue() || '')
          
          const handleKeyDown = async (e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              const nuevoValor = e.target.value.trim()
              const valorAnterior = cell.getValue() || ''
              
              // Validar que sea un número válido
              const valorNumerico = parseFloat(nuevoValor)
              if (nuevoValor !== '' && Number.isNaN(valorNumerico)) {
                toast.error('El valor FOB debe ser un número válido')
                return
              }
              
              const valorFinal = nuevoValor === '' ? '' : valorNumerico.toString()
              
              if (valorFinal === valorAnterior.toString()) {
                setEditingCell(null)
                return
              }
              
              const confirmar = window.confirm(
                `¿Estás seguro que deseas actualizar el FOB?\n\nValor anterior: ${valorAnterior || '(vacío)'}\nValor nuevo: ${valorFinal || '(vacío)'}`
              )
              
              if (confirmar) {
                try {
                  const response = await putActualizarFob(row.original.id, valorFinal || 0)
                  if (response) {
                    toast.success('FOB actualizado correctamente')
                    // Actualizar el item en la lista local
                    setItems(prevItems =>
                      prevItems.map(item =>
                        item.id === row.original.id
                          ? { ...item, fob: valorFinal || null }
                          : item
                      )
                    )
                    setEditingCell(null)
                  } else {
                    toast.error('Error al actualizar el FOB')
                    setEditingCell(null)
                  }
                } catch (error) {
                  console.error('Error al actualizar FOB:', error)
                  toast.error('Error al actualizar el FOB')
                  setEditingCell(null)
                }
              } else {
                setEditingCell(null)
              }
            } else if (e.key === 'Escape') {
              setEditingCell(null)
            }
          }
          
          const handleClick = () => {
            setEditingCell(cellKey)
            setEditingValue(cell.getValue() || '')
          }
          
          const handleBlur = () => {
            setEditingCell(null)
          }
          
          if (isEditing) {
            return (
              <input
                type="text"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                style={{
                  width: '100%',
                  border: '2px solid #667eea',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            )
          }
          
          return (
            <div
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick()
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                width: '100%',
                padding: '4px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                minHeight: '24px',
                border: '1px solid transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
                e.currentTarget.style.borderColor = '#e5e7eb'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.borderColor = 'transparent'
              }}
            >
              {currentValue || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Click para editar</span>}
            </div>
          )
        },
      },
      // 19. CANTIDAD
      {
        accessorKey: 'cantidad',
        header: 'Cantidad',
        size: 100,
        Cell: ({ cell, row }) => {
          const cellKey = `cantidad_${row.original.id}`
          const isEditing = editingCell === cellKey
          const currentValue = isEditing ? editingValue : (cell.getValue() !== undefined && cell.getValue() !== null && cell.getValue() !== '' ? cell.getValue() : 0)
          
          const handleKeyDown = async (e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              const nuevoValor = e.target.value.trim()
              const valorAnterior = cell.getValue()
              
              const valorEntero = parseInt(nuevoValor, 10)
              if (nuevoValor !== '' && (Number.isNaN(valorEntero) || valorEntero < 0)) {
                toast.error('La cantidad debe ser un número entero mayor o igual a 0')
                return
              }
              
              const valorFinal = nuevoValor === '' ? 0 : valorEntero
              const valorAnteriorNum = valorAnterior !== undefined && valorAnterior !== null && valorAnterior !== '' ? parseInt(valorAnterior, 10) : 0
              
              if (valorFinal === valorAnteriorNum) {
                setEditingCell(null)
                return
              }
              
              const confirmar = window.confirm(
                `¿Estás seguro que deseas actualizar la Cantidad?\n\nValor anterior: ${valorAnteriorNum}\nValor nuevo: ${valorFinal}`
              )
              
              if (confirmar) {
                try {
                  const response = await putActualizarCantidad(row.original.id, valorFinal)
                  if (response) {
                    toast.success('Cantidad actualizada correctamente')
                    setItems(prevItems =>
                      prevItems.map(item =>
                        item.id === row.original.id
                          ? { ...item, cantidad: valorFinal }
                          : item
                      )
                    )
                    setEditingCell(null)
                  } else {
                    toast.error('Error al actualizar la Cantidad')
                    setEditingCell(null)
                  }
                } catch (error) {
                  console.error('Error al actualizar Cantidad:', error)
                  toast.error('Error al actualizar la Cantidad')
                  setEditingCell(null)
                }
              } else {
                setEditingCell(null)
              }
            } else if (e.key === 'Escape') {
              setEditingCell(null)
            }
          }
          
          const handleClick = () => {
            setEditingCell(cellKey)
            setEditingValue(cell.getValue() !== undefined && cell.getValue() !== null && cell.getValue() !== '' ? String(cell.getValue()) : '0')
          }
          
          const handleBlur = () => {
            setEditingCell(null)
          }
          
          if (isEditing) {
            return (
              <input
                type="number"
                min="0"
                step="1"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                style={{
                  width: '100%',
                  border: '2px solid #667eea',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            )
          }
          
          return (
            <div
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick()
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                width: '100%',
                padding: '4px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                minHeight: '24px',
                border: '1px solid transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
                e.currentTarget.style.borderColor = '#e5e7eb'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.borderColor = 'transparent'
              }}
            >
              {currentValue !== '' && currentValue !== undefined && currentValue !== null ? currentValue : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Click para editar</span>}
            </div>
          )
        },
      },
      // 20. NOMBRE DE PROVEEDOR
      {
        accessorKey: 'nombre_proveedor',
        header: 'Nombre de Proveedor',
        size: 200,
      },
      // 20. CÓDIGO DE PROVEEDOR
      {
        accessorKey: 'codigo_proveedor',
        header: 'Código de Proveedor',
        size: 150,
      },

      {
        accessorKey: 'nombre_completo',
        header: 'Usuario Catálogo',
        size: 150,
      },
      // true or false 
      {
        accessorKey: 'descargado_excel',
        header: 'Descarga Excel',
        size: 150,
        Cell: ({ cell }) => (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {cell.getValue() ? 'Sí' : 'No'}
          </span>
        ),
      },
      // 21. MARCA
      {
        accessorKey: 'marca',
        header: 'Marca',
        size: 120,
      },
      // 22. MODELO DE APLICACIÓN (Pedido Inicial)
      {
        accessorKey: 'modelo_aplicacion',
        header: 'Modelo de Aplicación',
        size: 150,
      },
      {
        accessorKey: 'pedido_inicial',
        header: 'Pedido Inicial',
        size: 250,
      },
      // 24. PAÍS DE ORIGEN
      {
        accessorKey: 'pais_origen',
        header: 'País de Origen',
        size: 120,
      },
      // 25. SOLICITADO POR
      {
        accessorKey: 'solicitado_por',
        header: 'Solicitado Por',
        size: 130,
      },
      // 26. SAP ASIGNADO
      {
        accessorKey: 'sap_asignado',
        header: 'SAP Asignado',
        size: 120,
      },
      // 27. PLANIFICACIÓN (DISPO)
      {
        accessorKey: 'planificador',
        header: 'Planificación (DISPO)',
        size: 150,
      },
      // 28. GRUPO DE COMPRAS
      {
        accessorKey: 'grupo_compras',
        header: 'Grupo de Compras',
        size: 120,
      },
      // 29. DESCRIPCIÓN EXCEL (texto_breve_en)
    
      // 30. FECHA EDICION MANUAL
      {
        accessorKey: 'fecha_edicion_manual',
        header: 'Fecha Edición Manual',
        size: 180,
      },
      // 31. CREADO EN
      {
        accessorKey: 'creado_en',
        header: 'Creado En',
        size: 180,
      },
     
    ],
    [itemsFiltrados, itemsSeleccionados, editingCell, editingValue]
  )

  return (
    <div className="jerarquias-container">
      {/* Header */}
      <div className="jerarquias-header">
        <div className="jerarquias-header-content">
          <div className="jerarquias-title-section">
            <FiList className="jerarquias-title-icon" />
            <div>
              <h1 className="jerarquias-title">Jerarquías</h1>
              <p className="jerarquias-subtitle">
                Gestiona y visualiza todas las jerarquías de materiales ({itemsFiltrados.length} de {items.length} items)
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {(() => {
              const todosSeleccionados = itemsFiltrados.length > 0 && 
                                         itemsSeleccionados.length === itemsFiltrados.length
              
              return (
                <button
                  type="button"
                  onClick={() => handleSeleccionarTodos(itemsFiltrados)}
                  disabled={itemsFiltrados.length === 0}
                  style={{
                    background: todosSeleccionados 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    cursor: itemsFiltrados.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: itemsFiltrados.length === 0 
                      ? 'none' 
                      : '0 2px 8px rgba(102, 126, 234, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: itemsFiltrados.length === 0 ? 0.5 : 1,
                    transition: 'all 0.3s ease'
                  }}
                  title={
                    itemsFiltrados.length === 0 
                      ? 'No hay items disponibles para seleccionar'
                      : todosSeleccionados
                      ? 'Deseleccionar todos'
                      : 'Todo'
                  }
                >
                  {todosSeleccionados ? '✓ Todos Seleccionados' : `Seleccionar Todo (${itemsFiltrados.length})`}
                </button>
              )
            })()}
            {itemsSeleccionados.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleAbrirModalExportar}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FiDownload />
                  Exportar Seleccionados ({itemsSeleccionados.length})
                </button>
                <button
                  type="button"
                  onClick={descargarExcelNPFSeleccionados}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FiDownload />
                  Exportar (infaltable/garantia) ({itemsSeleccionados.length})
                </button>
              </>
            )}
            <input
              type="file"
              ref={inputArchivoNPFRef}
              accept=".csv,.xlsx,.xls"
              onChange={handleArchivoNPFChange}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => inputArchivoNPFRef.current?.click()}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FiUpload />
              Importar (infaltable/garantía)
            </button>
            <button
              type="button"
              onClick={handleClickImportarNPF}
              disabled={loadingImportarNPF}
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: loadingImportarNPF ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loadingImportarNPF ? 0.7 : 1
              }}
            >
              <FiUpload />
              {loadingImportarNPF ? 'Importando...' : 'Importar NPF'}
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="jerarquias-filtros">
        <div className="jerarquias-filtros-grupo">
          <span className="jerarquias-filtros-label">Jerarquía Aprobada:</span>
          <button
            type="button"
            className={`jerarquias-filtro-btn ${filtroAprobada === 'todos' ? 'active' : ''}`}
            onClick={() => setFiltroAprobada('todos')}
          >
            Todos ({items.length})
          </button>
          <button
            type="button"
            className={`jerarquias-filtro-btn ${filtroAprobada === 'si' ? 'active' : ''}`}
            onClick={() => setFiltroAprobada('si')}
          >
            ✓ Aprobadas ({items.filter(item => item.jerarquia_aprobada === 1).length})
          </button>
          <button
            type="button"
            className={`jerarquias-filtro-btn ${filtroAprobada === 'no' ? 'active' : ''}`}
            onClick={() => setFiltroAprobada('no')}
          >
            ✗ No Aprobadas ({items.filter(item => item.jerarquia_aprobada !== 1).length})
          </button>
        </div>
        <div className="jerarquias-filtros-grupo">
          <span className="jerarquias-filtros-label">Estado:</span>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              style={{
                padding: filtroEstado !== 'todos' ? '8px 35px 8px 35px' : '8px 35px 8px 12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                backgroundColor: filtroEstado === 'todos' ? '#fff' : '#f3f4f6',
                color: '#374151',
                minWidth: '220px',
                outline: 'none',
                transition: 'all 0.2s ease',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23374151\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '35px'
              }}
            >
              <option value="todos">Todos</option>
              <option value="no_existe">
                No Existe ({items.filter(item => item.estado === 'no_existe').length})
              </option>
              <option value="existe_previamente">
                Existe Previamente ({items.filter(item => item.estado === 'existe_previamente').length})
              </option>
              <option value="creado">
                Creado ({items.filter(item => item.estado === 'creado').length})
              </option>
            </select>
            {filtroEstado !== 'todos' && (
              <div
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
              >
                {(() => {
                  const estadoInfo = getEstadoInfo(filtroEstado)
                  const IconComponent = estadoInfo.icon
                  return <IconComponent size={16} color={estadoInfo.color} />
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="jerarquias-content">
        <div className="jerarquias-table-wrapper">
          <MaterialReactTable
            columns={columns}
            data={itemsFiltrados}
            enableColumnResizing
            enableColumnOrdering
            enablePinning
            enableStickyHeader
            enableDensityToggle
            enableFullScreenToggle
            enableGlobalFilter
            initialState={{
              density: 'compact',
              showGlobalFilter: true,
              columnPinning: { left: ['seleccionar', 'infaltable', 'garantia', 'accion', 'id'] },
            }}
            state={{
              isLoading: loading,
            }}
            muiTableContainerProps={{
              sx: { maxHeight: 'calc(100vh - 250px)' }
            }}
            muiTablePaperProps={{
              sx: {
                boxShadow: 'none',
                border: '1px solid #e5e7eb',
              }
            }}
          />
        </div>

        <div className="jerarquias-imagenes-module">
          <div className="jerarquias-imagenes-module-header">
            <button
              type="button"
              onClick={handleTogglePanelImagenes}
              className={`jerarquias-imagenes-toggle-btn ${imagenesPanelVisible ? 'active' : ''}`}
            >
              <FiImage size={16} />
              {imagenesPanelVisible ? 'Ocultar Imágenes' : 'Imágenes'}
            </button>
          </div>

          {imagenesPanelVisible && (
            <div className="jerarquias-imagenes-panel">
              <div className="jerarquias-imagenes-panel-top">
                <div className="jerarquias-imagenes-panel-title-group">
                  <h3 className="jerarquias-imagenes-panel-title">Imágenes por material</h3>
                  <p className="jerarquias-imagenes-panel-subtitle">
                    Digita un id de material y presiona buscar para consultar imágenes.
                  </p>
                </div>
              </div>

              <div className="jerarquias-imagenes-filter-row">
                <label htmlFor="imagenes-material-filter" className="sr-only">
                  Buscar por id de material
                </label>
                <input
                  id="imagenes-material-filter"
                  type="text"
                  value={imagenesFiltroId}
                  onChange={(e) => setImagenesFiltroId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleBuscarImagenes()
                    }
                  }}
                  placeholder="Buscar por id de material..."
                  className="jerarquias-imagenes-filter-input"
                />
                <button
                  type="button"
                  onClick={handleBuscarImagenes}
                  disabled={imagenesLoading}
                  className="jerarquias-imagenes-search-btn"
                  title="Buscar imágenes por id"
                >
                  <FiSearch size={15} />
                  {imagenesLoading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>

              {imagenesLoading && (
                <div className="jerarquias-imagenes-state">
                  <p>Cargando imágenes por material...</p>
                </div>
              )}

              {!imagenesLoading && imagenesError && (
                <div className="jerarquias-imagenes-state error">
                  <p>{imagenesError}</p>
                  <button type="button" onClick={cargarImagenesPorMaterial} className="jerarquias-imagenes-retry-btn">
                    Reintentar
                  </button>
                </div>
              )}

              {!imagenesLoading && !imagenesError && !imagenesBusquedaEjecutada && (
                <div className="jerarquias-imagenes-state">
                  <p>Ingresa un id de material y presiona Buscar para ver resultados.</p>
                </div>
              )}

              {!imagenesLoading && !imagenesError && imagenesBusquedaEjecutada && materialesFiltradosImagenes.length === 0 && (
                <div className="jerarquias-imagenes-state">
                  <p>No se encontraron imágenes para el id consultado.</p>
                </div>
              )}

              {!imagenesLoading && !imagenesError && imagenesBusquedaEjecutada && materialesFiltradosImagenes.length > 0 && (
                <div className="jerarquias-material-cards-grid">
                  {materialesFiltradosImagenes.map((material) => (
                    <article key={material.materialId} className="jerarquias-material-card">
                      <header className="jerarquias-material-card-header">
                        <div>
                          <h4 className="jerarquias-material-id">{material.materialId}</h4>
                          <span className="jerarquias-material-count">
                            {material.imagenes.length} imagen{material.imagenes.length !== 1 ? 'es' : ''}
                          </span>
                        </div>
                      </header>

                      {material.imagenes.length === 0 ? (
                        <div className="jerarquias-material-empty">Este material no tiene imágenes disponibles.</div>
                      ) : (
                        <div className="jerarquias-material-images-grid">
                          {material.imagenes.map((img, index) => (
                            <div key={img.id} className="jerarquias-material-image-item">
                              <img
                                src={img.publicUrl}
                                alt={`Material ${material.materialId} imagen ${index + 1}`}
                                className="jerarquias-material-image"
                              />
                              <div className="jerarquias-material-image-actions">
                                <a
                                  href={img.publicUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="jerarquias-image-action-link"
                                  title="Abrir en nueva pestaña"
                                >
                                  <FiExternalLink size={14} />
                                  Abrir
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleCopiarEnlace(img.publicUrl)}
                                  className="jerarquias-image-action-btn"
                                  title="Copiar enlace"
                                >
                                  <FiCopy size={14} />
                                  Copiar enlace
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Jerarquía */}
      <Modal
        title={null}
        open={modalVisible}
        onCancel={handleCerrarModal}
        footer={null}
        width={600}
        className="jerarquias-modal"
        closeIcon={<FiX />}
      >
        {itemSeleccionado && (
          <div className="jerarquias-modal-content">
            <div className="jerarquias-modal-header">
              <div>
                <h2 className="jerarquias-modal-title">Material {itemSeleccionado.id}</h2>
                <p className="jerarquias-modal-subtitle">{itemSeleccionado.texto_breve_es}</p>
              </div>
              {itemSeleccionado.gs_url && (
                <button
                  type="button"
                  onClick={handleCargarImagen}
                  disabled={loadingImagen}
                  className="jerarquias-image-btn"
                  title="Ver imagen del material"
                  style={{
                    position: 'absolute',
                    top: '20px',
                    right: '50px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    cursor: loadingImagen ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    opacity: loadingImagen ? 0.6 : 1,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <FiSearch size={16} />
                  {loadingImagen ? 'Cargando...' : 'Ver Imagen'}
                </button>
              )}
            </div>

            <div className="jerarquias-modal-body">
              {imagenUrl && (
                <div style={{
                  marginBottom: '20px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Vista previa de la imagen
                  </div>
                  <div style={{
                    padding: '15px',
                    textAlign: 'center',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    <img 
                      src={imagenUrl} 
                      alt="Vista previa del material"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                      onError={(e) => {
                        console.error('Error al cargar la imagen')
                        toast.error('Error al cargar la imagen')
                        e.target.style.display = 'none'
                      }}
                    />
                  </div>
                </div>
              )}
              

              <div className="jerarquias-niveles">
                {nivelesConfig.map((nivel) => {
                  const valorActual = valoresSeleccionados[nivel.key]
                  console.log(`${nivel.label} - Valor seleccionado:`, valorActual)
                  console.log(`${nivel.label} - Opciones disponibles:`, nivel.opciones.map(o => o[nivel.campo]))
                  
                  // Para Familia, usar input con datalist y filtro de mínimo 3 letras
                  if (nivel.key === 'familia') {
                    const familiasFiltradas = busquedaFamilia.length >= 3
                      ? familias.filter(f => 
                          f.nombre.toLowerCase().includes(busquedaFamilia.toLowerCase())
                        )
                      : []
                    
                    const familiaSeleccionada = familias.find(f => f.nombre === valorActual)
                    const displayValue = familiaSeleccionada 
                      ? familiaSeleccionada.nombre 
                      : busquedaFamilia
                    
                    return (
                      <div key={`nivel-${nivel.key}`} className="jerarquias-nivel-item">
                        <div className="jerarquias-nivel-header">
                          <span className="jerarquias-nivel-label-badge">{nivel.label}</span>
                          {busquedaFamilia.length > 0 && busquedaFamilia.length < 3 && (
                            <span style={{ 
                              fontSize: '11px', 
                              color: '#f59e0b', 
                              marginLeft: '8px',
                              fontStyle: 'italic'
                            }}>
                              Escribe al menos 3 letras para buscar
                            </span>
                          )}
                          {busquedaFamilia.length >= 3 && familiasFiltradas.length > 0 && (
                            <span style={{ 
                              fontSize: '11px', 
                              color: '#10b981', 
                              marginLeft: '8px'
                            }}>
                              {familiasFiltradas.length} resultado{familiasFiltradas.length !== 1 ? 's' : ''} encontrado{familiasFiltradas.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div className="jerarquias-nivel-input-wrapper">
                          <label 
                            htmlFor={`familia-input-${itemSeleccionado?.id || 'new'}`}
                            style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}
                          >
                            Buscar Familia
                          </label>
                          <input
                            id={`familia-input-${itemSeleccionado?.id || 'new'}`}
                            aria-label="Buscar Familia"
                            type="text"
                            value={displayValue}
                            onChange={(e) => {
                              const nuevoValor = e.target.value
                              setBusquedaFamilia(nuevoValor)
                              
                              // Si el usuario borra todo, limpiar la selección
                              if (nuevoValor === '') {
                                handleChangeSelect(nivel.key, '')
                              }
                            }}
                            onInput={(e) => {
                              // Cuando el usuario selecciona del datalist, el evento input se dispara
                              const inputValue = e.target.value
                              const familiaEncontrada = familias.find(f => 
                                f.nombre === inputValue
                              )
                              if (familiaEncontrada) {
                                handleChangeSelect(nivel.key, familiaEncontrada.nombre)
                                setBusquedaFamilia(familiaEncontrada.nombre)
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value.trim()
                              
                              // Restaurar el borde
                              e.target.style.borderColor = '#e5e7eb'
                              
                              // Si hay un valor seleccionado y el input no coincide, mantener el seleccionado
                              if (valorActual && inputValue !== valorActual) {
                                setBusquedaFamilia(valorActual)
                                return
                              }
                              
                              if (!inputValue) {
                                setBusquedaFamilia('')
                                handleChangeSelect(nivel.key, '')
                                return
                              }
                              
                              // Si el valor del input coincide con una opción, seleccionarla
                              if (inputValue.length >= 3) {
                                const familiaEncontrada = familias.find(f => 
                                  f.nombre.toLowerCase() === inputValue.toLowerCase()
                                )
                                if (familiaEncontrada) {
                                  handleChangeSelect(nivel.key, familiaEncontrada.nombre)
                                  setBusquedaFamilia(familiaEncontrada.nombre)
                                } else if (valorActual) {
                                  // Si no coincide exactamente, mantener el valor anterior si existe
                                  setBusquedaFamilia(valorActual)
                                }
                              }
                            }}
                            list={`familias-list-${itemSeleccionado?.id || 'new'}`}
                            placeholder={busquedaFamilia.length < 3 
                              ? "Escribe al menos 3 letras para buscar..." 
                              : "Buscar familia..."}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              outline: 'none',
                              transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#667eea'
                            }}
                          />
                          {busquedaFamilia.length >= 3 && (
                            <datalist id={`familias-list-${itemSeleccionado?.id || 'new'}`} aria-label="Opciones de familia">
                              {familiasFiltradas.slice(0, 100).map((familia) => (
                                <option 
                                  key={familia.id || familia.nombre} 
                                  value={familia.nombre}
                                />
                              ))}
                            </datalist>
                          )}
                        </div>
                      </div>
                    )
                  }
                  
                  // Para los demás niveles, mantener el select normal
                  return (
                    <div key={`nivel-${nivel.key}`} className="jerarquias-nivel-item">
                      <div className="jerarquias-nivel-header">
                        <span className="jerarquias-nivel-label-badge">{nivel.label}</span>
                      </div>
                      <div className="jerarquias-nivel-input-wrapper">
                        <select
                          value={valorActual}
                          onChange={(e) => handleChangeSelect(nivel.key, e.target.value)}
                          className="jerarquias-nivel-select"
                        >
                          <option value="">Seleccionar {nivel.label.toLowerCase()}</option>
                          {nivel.opciones.map((opcion) => (
                            <option key={opcion.id || opcion.nombre} value={opcion[nivel.campo]}>
                              {opcion[nivel.campo]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="jerarquias-modal-info">
                <div className="jerarquias-info-item">
                  <span className="jerarquias-info-label">Jerarquía completa:</span>
                  <span className="jerarquias-info-value">{itemSeleccionado.jerarquia || 'Sin jerarquía'}</span>
                </div>
                <div className="jerarquias-info-item">
                  <span className="jerarquias-info-label">Aprobada:</span>
                  <span className={`jerarquias-info-badge ${itemSeleccionado.jerarquia_aprobada ? 'approved' : 'pending'}`}>
                    {itemSeleccionado.jerarquia_aprobada ? 'Sí' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <div className="jerarquias-modal-footer">
              <button
                type="button"
                onClick={handleCerrarModal}
                className="jerarquias-modal-btn jerarquias-modal-btn-close"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={handleAprobarJerarquia}
                disabled={itemSeleccionado.jerarquia_aprobada === 1}
                className={`jerarquias-modal-btn jerarquias-modal-btn-accept ${
                  itemSeleccionado.jerarquia_aprobada === 1 ? 'disabled' : ''
                }`}
                title={
                  itemSeleccionado.jerarquia_aprobada === 1
                    ? 'Esta jerarquía ya está aprobada'
                    : 'Aprobar jerarquía'
                }
              >
                {itemSeleccionado.jerarquia_aprobada === 1 ? '✓ Aprobada' : 'Aprobar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Confirmación de Exportación */}
      <Modal
        title="Confirmar Exportación"
        open={modalExportarVisible}
        onOk={handleExportarItems}
        onCancel={() => setModalExportarVisible(false)}
        okText="Exportar"
        cancelText="Cancelar"
        confirmLoading={loadingExportar}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            ¿Estás seguro que quieres exportar los siguientes materiales?
          </p>
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: '#f9fafb',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {itemsSeleccionados.map(itemId => {
                const item = items.find(i => i.id === itemId)
                return item ? (
                  <li key={itemId} style={{ marginBottom: '8px', fontSize: '14px' }}>
                    <strong>ID {item.id}:</strong> {item.texto_breve_es || 'Sin descripción'}
                  </li>
                ) : null
              })}
            </ul>
          </div>
          <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
            Se exportarán <strong>{itemsSeleccionados.length}</strong> items a Excel.
          </p>
        </div>
      </Modal>

      {/* Modal de Información SAP */}
      <Modal
        title="Información SAP"
        open={modalSapVisible}
        onCancel={() => {
          setModalSapVisible(false)
          setItemSapSeleccionado(null)
        }}
        footer={[
          <button
            key="close"
            type="button"
            onClick={() => {
              setModalSapVisible(false)
              setItemSapSeleccionado(null)
            }}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cerrar
          </button>
        ]}
        width={600}
        closeIcon={<FiX />}
      >
        {itemSapSeleccionado && (
          <div style={{ padding: '20px 0' }}>
            <div style={{
              display: 'grid',
              gap: '20px'
            }}>
              <div style={{
                padding: '15px',
                backgroundColor: '#e2edf9',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Texto Breve
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  wordBreak: 'break-word'
                }}>
                  {itemSapSeleccionado.texto_breve_es || 'N/A'}
                </div>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#e2edf9',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Grupo de Artículo Sugerido
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  wordBreak: 'break-word'
                }}>
                  {itemSapSeleccionado.grupo_articulo || 'N/A'}
                </div>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Descripción SAP
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  wordBreak: 'break-word'
                }}>
                  {itemSapSeleccionado.descripcion_sap || 'N/A'}
                </div>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Grupo Artículos SAP
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  wordBreak: 'break-word'
                }}>
                  {itemSapSeleccionado.grupo_articulos_sap || 'N/A'}
                </div>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Grupo Compras SAP
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  wordBreak: 'break-word'
                }}>
                  {itemSapSeleccionado.grupo_compras_sap || 'N/A'}
                </div>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Fecha Creación SAP
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  wordBreak: 'break-word'
                }}>
                  {itemSapSeleccionado.fecha_creacion_sap || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default AxiomaJerarquiasContainer

